import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../state/customerAuth';
import '../styles/account.css';

export function AccountPage() {
  const { isAuthenticated, booting, login, register } = useCustomerAuth();
  const location = useLocation();
  const redirectToRaw = location?.state?.redirectTo;
  const redirectTo =
    typeof redirectToRaw === 'string' && redirectToRaw.startsWith('/') ? redirectToRaw : null;
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  if (booting) {
    return (
      <main className="accountPage">
        <div className="accountPage__container" style={{ padding: 48, justifyContent: 'center' }}>
          <p className="accountPage__subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Loading your session…
          </p>
        </div>
      </main>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo || '/account/dashboard'} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (mode === 'signup') {
      if (password.length < 8) {
        setFormError('Password must be at least 8 characters.');
        return;
      }
      if (password !== password2) {
        setFormError('Passwords do not match.');
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register({
          name: name.trim(),
          email: email.trim(),
          password,
          phone: null,
        });
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="accountPage">
      <div className="accountPage__container">
        <div className="accountPage__brand">
          <div className="accountPage__brandInner">
            <div className="accountPage__logo">✦</div>
            <h2 className="accountPage__tagline">Welcome to Elenard</h2>
            <p className="accountPage__brandDesc">
              Discover handcrafted hair jewelry that tells your story.
            </p>
          </div>
        </div>

        <div className="accountPage__formPanel">
          <div className="accountPage__formWrap">
            <h1 className="accountPage__title">{mode === 'login' ? 'Sign in' : 'Create account'}</h1>
            <p className="accountPage__subtitle">
              {mode === 'login'
                ? 'Welcome back! Enter your details to continue.'
                : 'Join us to track orders and speed up checkout.'}
            </p>

            <div className="accountPage__tabs">
              <button
                className={`accountPage__tab${mode === 'login' ? ' accountPage__tab--active' : ''}`}
                onClick={() => {
                  setMode('login');
                  setFormError('');
                }}
                type="button"
              >
                Log in
              </button>
              <button
                className={`accountPage__tab${mode === 'signup' ? ' accountPage__tab--active' : ''}`}
                onClick={() => {
                  setMode('signup');
                  setFormError('');
                }}
                type="button"
              >
                Sign up
              </button>
            </div>

            {formError ? (
              <p className="accountPage__formError" role="alert" style={{ marginBottom: 12 }}>
                {formError}
              </p>
            ) : null}

            <form className="accountPage__form" onSubmit={onSubmit}>
              {mode === 'signup' && (
                <div className="accountPage__field">
                  <label className="accountPage__label" htmlFor="acc_name">
                    Full name
                  </label>
                  <input
                    id="acc_name"
                    className="accountPage__input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
              )}

              <div className="accountPage__field">
                <label className="accountPage__label" htmlFor="acc_email">
                  Email address
                </label>
                <input
                  id="acc_email"
                  className="accountPage__input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>

              <div className="accountPage__field">
                <div className="accountPage__labelRow">
                  <label className="accountPage__label" htmlFor="acc_password">
                    Password
                  </label>
                  {mode === 'login' && (
                    <span className="accountPage__forgotLink" style={{ opacity: 0.5 }}>
                      Forgot? Use Contact
                    </span>
                  )}
                </div>
                <div className="accountPage__inputWrap">
                  <input
                    id="acc_password"
                    className="accountPage__input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    className="accountPage__eyeBtn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <div className="accountPage__field">
                  <label className="accountPage__label" htmlFor="acc_password2">
                    Confirm password
                  </label>
                  <input
                    id="acc_password2"
                    className="accountPage__input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                </div>
              )}

              <button className="accountPage__submit" type="submit" disabled={submitting}>
                {submitting ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </form>

            <p className="accountPage__switch">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                className="accountPage__switchLink"
                onClick={() => {
                  setMode(mode === 'login' ? 'signup' : 'login');
                  setFormError('');
                }}
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>

            <p className="accountPage__switch" style={{ marginTop: 16 }}>
              <Link to="/shop" className="accountPage__switchLink" style={{ textDecoration: 'none' }}>
                ← Continue shopping
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
